"use client";

import type { ReactNode } from "react";
import { createContext, use, useEffect, useMemo, useState } from "react";
import { WISHLIST_ID_MAX, WISHLIST_MAX } from "./wishlist-limits";

interface WishlistValue {
	/** Product ids, oldest first. Names, prices and images are never stored. */
	ids: string[];
	count: number;
	/** The stored ids have been read, so an empty `ids` really is empty. */
	ready: boolean;
	has: (productId: string) => boolean;
	toggle: (productId: string) => void;
	remove: (productId: string) => void;
	/** Drop ids the catalogue no longer publishes, answered by `/wishlist`'s read. */
	forget: (productIds: readonly string[]) => void;
}

const WishlistContext = createContext<WishlistValue | null>(null);
const STORAGE_KEY = "jana_wishlist";

/**
 * The stored value holds product ids ONLY: a snapshot of the name, image or
 * price goes stale the moment the merchant edits the product, so `/wishlist`
 * reads those from the catalogue on every visit. An entry written before this
 * rule is an object carrying `productId`, and only that field is kept. The
 * value is client-writable, so anything else is discarded.
 */
function parseStored(raw: string): string[] {
	const parsed: unknown = JSON.parse(raw);
	if (!Array.isArray(parsed)) return [];
	const ids = parsed.map((i: unknown) => {
		if (typeof i === "string") return i;
		if (
			typeof i === "object" &&
			i !== null &&
			typeof (i as { productId?: unknown }).productId === "string"
		)
			return (i as { productId: string }).productId;
		return null;
	});
	return [
		...new Set(
			ids.filter((i): i is string => i !== null && i.length > 0 && i.length <= WISHLIST_ID_MAX),
		),
	].slice(-WISHLIST_MAX);
}

export function WishlistProvider({ children }: { children: ReactNode }) {
	const [ids, setIds] = useState<string[]>([]);
	const [hydrated, setHydrated] = useState(false);

	useEffect(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) setIds(parseStored(raw));
		} catch {
			/* ignore */
		}
		setHydrated(true);
	}, []);

	useEffect(() => {
		if (!hydrated) return;
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
		} catch {
			/* private browsing / storage disabled — the wishlist stays in memory */
		}
	}, [ids, hydrated]);

	const value = useMemo<WishlistValue>(
		() => ({
			ids,
			count: ids.length,
			ready: hydrated,
			has: (id) => ids.includes(id),
			toggle: (id) =>
				setIds((prev) =>
					prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id].slice(-WISHLIST_MAX),
				),
			remove: (id) => setIds((prev) => prev.filter((i) => i !== id)),
			forget: (productIds) => {
				const gone = new Set(productIds);
				setIds((prev) => (prev.some((i) => gone.has(i)) ? prev.filter((i) => !gone.has(i)) : prev));
			},
		}),
		[ids, hydrated],
	);

	return <WishlistContext value={value}>{children}</WishlistContext>;
}

export function useWishlist(): WishlistValue {
	const ctx = use(WishlistContext);
	if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
	return ctx;
}
